import { useCallback, useEffect, useRef, useState } from 'react';

import {
  btag,
  Bus,
  constants,
  NotificationManager,
  PaymentV2Api,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';

import {
  ACCOUNT_STATUSES,
  bonusStatuses,
  DATA_LAYER_STORAGE_KEYS,
  POSTHOG_CTA,
} from 'common/constants';
import { useQuickDepositBonusesContext } from 'src/common/Popup/QuickDepositPopup/QuickDepositBonusesProvider';
import {
  buildPaymentUrl,
  showAlternativeMethodsOrError,
} from 'src/common/Popup/alternativePaymentUtils';
import { depositSubmit } from 'src/helpers/depositSubmit';

export const useDepositSubmit = ({
  activatedBonus,
  browser,
  closeQRCodeOnSuccess,
  history,
  ip,
  locale,
  onClose,
  onDepositStart,
  onDepositSuccess,
  openSignupPopup,
  os,
  paymentMethod,
  setPaymentUrl,
  setRedirectUrl,
  showInPopup,
  showSelfRegulationPopupIfNeeded,
  showSpentLimitPopupIfNeeded,
  value,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [depositPopupId, setDepositPopupId] = useState(null);
  const loadingNotificationId = useRef();
  const { intendedBonusRef, selectedBonus } = useQuickDepositBonusesContext();
  const {
    account,
    wallet: { currency, detailedBalance },
  } = useApplicationState();
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;

  const clientProfilePath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CLIENT_PROFILE);
  const depositPageUrl = `${clientProfilePath}/${clientProfileRoutes.payment}/${paymentRoutes.deposit}`;

  const onCreateDeposit = useCallback(() => {
    loadingNotificationId.current = NotificationManager.display({
      data: {
        headerTitle: 'deposit_loading',
        text: 'deposit_loading_text',
        title: 'deposit_loading_title',
      },
      type: 'paymentLoading',
    });
  }, []);

  const onDepositStatusError = useCallback(() => {
    Bus.emit('ui:depositErrorHandled');

    NotificationManager.removeNotification(loadingNotificationId.current);

    setDepositPopupId(loadingNotificationId.current);

    const fallbackError = () => {
      NotificationManager.display({
        data: {
          className: 'deposit-failed-popup',
          headerTitle: 'deposit',
          text: 'deposit_failed_message',
          title: 'deposit_failed_title',
        },
        type: 'errorTransaction',
      });
    };

    if (!paymentMethod?.alternativePmEnabled) {
      fallbackError();
      return;
    }

    showAlternativeMethodsOrError({
      fallbackError,
      onSelectMethod: method =>
        history.push(buildPaymentUrl(`${depositPageUrl}/${method.id}`, value, method)),
      onViewAll: () => history.push(buildPaymentUrl(depositPageUrl, value, null)),
      operationType: 'DEPOSIT',
      pmId: paymentMethod.id,
    });
  }, [depositPageUrl, history, paymentMethod, setDepositPopupId, value]);

  useEffect(() => {
    if (!depositPopupId || !closeQRCodeOnSuccess) return;

    if (balance) {
      const timeoutId = setTimeout(() => {
        NotificationManager.removeNotification(depositPopupId);
        setDepositPopupId(null);
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [balance, closeQRCodeOnSuccess, depositPopupId]);

  const handleDeposit = useCallback(async () => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      if (showInPopup) {
        openSignupPopup();
      } else {
        history.push(staticRoutes.signup);
      }
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    if (!SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED)) {
      pushToDataLayer(
        'qd_deposit_button_click',
        {
          OS: os,
          amount: value,
          bonus_toggle_enabled: !!activatedBonus,
          browser,
          created_at: Date.now(),
          device: constants.DEVICE_TYPES.MOBILE,
          ip,
          locale,
          page_referer: window.location.pathname,
          payment_method: paymentMethod?.name,
          user_id: account.id,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.QUICK_DEPOSIT_BUTTON_CLICKED, true);
    }

    const bonus = selectedBonus || activatedBonus;
    const isBonusInstance = bonus?.status === bonusStatuses.ACTIVATED;
    const resolvedBonusId = isBonusInstance ? bonus?.bonus?.id : bonus?.id;
    const bonusId = bonus ? resolvedBonusId : null;

    const options = {
      amount: value,
      currency: currency,
      pmId: paymentMethod.id,
      ...(bonus &&
        !bonus.disabled &&
        !intendedBonusRef.current && {
          additionalAttributes: {
            bonusId: resolvedBonusId,
            bonusInstanceId: isBonusInstance ? bonus.id : null,
          },
        }),
    };

    onDepositStart?.({
      amount: value,
      bonus_id: bonusId,
      cta_clicked: POSTHOG_CTA.DEPOSIT,
      payment_method: paymentMethod?.name ?? null,
    });

    setLoading(true);

    PaymentV2Api.getPaymentData({
      onCreatePayment: onCreateDeposit,
      onPaymentStatusError: onDepositStatusError,
      paymentOptions: options,
      paymentType: PaymentV2Api.PaymentType.DEPOSIT,
    })
      .then(data => {
        NotificationManager.removeNotification(loadingNotificationId.current);

        pushToDataLayer(
          'deposit_created',
          {
            amount: value,
            client: {
              id: account.id,
            },
            created_at: Date.now(),
            currency: currency,
            payment_method: data.paymentName,
          },
          btag,
        );

        onDepositSuccess?.({ transaction_id: data.publicPaymentId });

        const popupNotificationId = depositSubmit(
          data,
          paymentMethod,
          setPaymentUrl,
          value,
          setRedirectUrl,
        );
        setDepositPopupId(popupNotificationId);

        onClose?.();
      })
      .catch(error => {
        const { code } = error;

        if (code) {
          setError({ message: code });
        }
      })
      .finally(() => setLoading(false));
  }, [
    account.id,
    account.status,
    activatedBonus,
    browser,
    currency,
    history,
    intendedBonusRef,
    ip,
    locale,
    onClose,
    onCreateDeposit,
    onDepositStart,
    onDepositStatusError,
    onDepositSuccess,
    openSignupPopup,
    os,
    paymentMethod,
    selectedBonus,
    setPaymentUrl,
    setRedirectUrl,
    showInPopup,
    showSelfRegulationPopupIfNeeded,
    showSpentLimitPopupIfNeeded,
    value,
  ]);

  return {
    error,
    handleDeposit,
    loading,
    setError,
  };
};
